import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, switchMap } from "rxjs";
import * as fromActions from "@app/store/insights/actions";
import * as fromSelector from "@app/store/insights/selector";
import { InsightsKeyword, InsightsKeywordState } from "@app/store/insights/state";
import { UserState } from "@app/store/user/state";
import { selectUserName } from "@app/store/user/selector";
import { BaseComponent } from "@app/components/base.component";
import { UDFApiService } from "@app/services/udf-api.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { environment } from "@environments/environment";

/**
 * Dashboard to list all kinds of analytics such as the search keyword. 
 *
 * @export InsightsDashboardPageComponent
 * @class InsightsDashboardPageComponent
 * @implements {OnInit}
 */
@Component({
    selector: "insights-dashboard-page",
    templateUrl: "./insights-dashboard-page.component.html",
    styleUrl: "./insights-dashboard-page.component.scss",
})
export class InsightsDashboardPageComponent extends BaseComponent implements OnInit {
    private destroyServiceRef = inject(DestroyRef);
    rootUrl: string = "";
    keywords$: Observable<InsightsKeyword[]>;
    loading$: Observable<boolean>;
    error$: Observable<string | null>;
    data: InsightsKeyword[];
    keywordList: InsightsKeyword[];
    private user: any;
    private userGroupList: Array<string>;
    allowed: boolean = false;
    
    constructor(
        private userStore: Store<{ userState: UserState }>,
        private store: Store<InsightsKeywordState>,
        private udfApiService: UDFApiService
    ) {
        super();
        this.keywords$ = this.store.select(fromSelector.selectKeywords);
        this.loading$ = this.store.select(fromSelector.selectLoading);
        this.error$ = this.store.select(fromSelector.selectError);
    }

    ngOnInit(): void {
        this.store.dispatch(fromActions.getInsightsKeywords());
        
        // --
        // 🔒 Only authorized users are allowed to access the dashboard page 
        //                                                                  -- //
        this.subscribeToStore(this.userStore.select(selectUserName), un => {
            if (un) {
                this.udfApiService.getGraphUser(un)
                .pipe(takeUntilDestroyed(this.destroyServiceRef), 
                switchMap(u => {
                    const oid = u.value.map(p => p.id);
                    return this.udfApiService.getGraphMemberOf(oid);
                })
                ).subscribe(memberOf => {
                    this.allowed = this.findOne(memberOf);
                })
            }
        });
    }

    /**
     * Determine if an array contains one or more items from another array.
     * 
     * @param {array} haystack the array to search.
     * @return {boolean} true|false if haystack contains at least one item from arr.
     */
    findOne(haystack: Array<string>): boolean {
        const arr = environment.authorizedUserGroups; 
        return arr.some(v => haystack.includes(v));
    }
}
